import { formatDate, formatNumber } from "$root/localization/formatters";
import { type KeyOrPathArray, accessByPath, pathToStr } from "$root/utils/objects";
import type { TableColumn } from "@mdotm/shadcn/legacy/components";
import type { ReactNode } from "react";
import { TinyTableDataCell, TinyTableHeadCell } from "./tiny-table";

/**
 * Print-flavoured counterpart to `$root/ui-lib/common-table-columns`.
 *
 * These builders return the shared parts of a print `TableColumn` (header/content rendered through
 * the tiny-table cells, alignment, tabular-nums for numeric columns). Callers spread the result and
 * add the layout bits that vary per table — `width`/`minWidth`/`maxWidth`, `headerCellStyle`, etc.:
 *
 * ```tsx
 * { ...commonReportColumns.date("date", "Date"), width: 96, maxWidth: 96 }
 * ```
 *
 * Columns are non-orderable by default since report tables are static; pass `orderable` to override.
 */

const PLACEHOLDER = "-";

/** The parts of a print `TableColumn` produced by these builders. Spread it, then add `width` etc. */
export type ReportColumnParts<T> = Pick<
	TableColumn<T, string>,
	"name" | "header" | "content" | "cellClassName" | "align" | "orderable"
>;

function reportHeader<T>(header: ReactNode): TableColumn<T, string>["header"] {
	return (headerProps) => <TinyTableHeadCell {...headerProps}>{header}</TinyTableHeadCell>;
}

/** Shared body of {@link commonReportColumns.float} / {@link commonReportColumns.integer}. */
function numericColumn<T>(
	path: KeyOrPathArray<T>,
	header: ReactNode,
	decimalPlaces: number | ((row: T) => number | undefined) | undefined,
	suffix: string | ((row: T) => string) | undefined,
): ReportColumnParts<T> {
	return {
		name: pathToStr(path),
		orderable: false,
		align: "end",
		cellClassName: "tabular-nums",
		header: reportHeader<T>(header),
		content: (row, props) => {
			const dp = typeof decimalPlaces === "function" ? decimalPlaces(row) : (decimalPlaces ?? 2);
			const sfx = typeof suffix === "function" ? suffix(row) : (suffix ?? "");
			return (
				<TinyTableDataCell
					{...props}
				>{`${formatNumber(accessByPath(row, path) as number, dp)}${sfx}`}</TinyTableDataCell>
			);
		},
	};
}

export const commonReportColumns = {
	string<T>(path: KeyOrPathArray<T>, header: ReactNode): ReportColumnParts<T> {
		return {
			name: pathToStr(path),
			orderable: false,
			header: reportHeader<T>(header),
			content: (row, props) => {
				const val = accessByPath(row, path);
				return <TinyTableDataCell {...props}>{val == null ? PLACEHOLDER : String(val)}</TinyTableDataCell>;
			},
		};
	},
	date<T>(path: KeyOrPathArray<T>, header: ReactNode): ReportColumnParts<T> {
		return {
			name: pathToStr(path),
			orderable: false,
			align: "end",
			cellClassName: "tabular-nums",
			header: reportHeader<T>(header),
			content: (row, props) => {
				const val = accessByPath(row, path);
				return (
					<TinyTableDataCell {...props}>
						{val ? formatDate(new Date(val as string | number)) : PLACEHOLDER}
					</TinyTableDataCell>
				);
			},
		};
	},
	/**
	 * Decimal-number column (mirrors `commonColumns.float`). `decimalPlaces` (default 2) and the
	 * optional `suffix` each accept a constant or a per-row function — the latter covers tables whose
	 * precision/unit varies by row (e.g. a per-row `unitOfMeasurement`).
	 *
	 * Follows the report convention `` `${formatNumber(value, dp)}${suffix}` ``: a nullish value relies
	 * on `formatNumber`'s `"-"` fallback and still gets the suffix appended (so e.g. `"-%"`), matching
	 * the existing metric tables. Use {@link currencyAmount} when a missing value should be a bare `"-"`.
	 */
	float<T>(
		path: KeyOrPathArray<T>,
		header: ReactNode,
		{
			decimalPlaces,
			suffix,
		}: { decimalPlaces?: number | ((row: T) => number | undefined); suffix?: string | ((row: T) => string) } = {},
	): ReportColumnParts<T> {
		return numericColumn(path, header, decimalPlaces, suffix);
	},
	/** Integer column (mirrors `commonColumns.integer`): always formats with 0 decimal places. */
	integer<T>(
		path: KeyOrPathArray<T>,
		header: ReactNode,
		{ suffix }: { suffix?: string | ((row: T) => string) } = {},
	): ReportColumnParts<T> {
		return numericColumn(path, header, 0, suffix);
	},
	percentage<T>(path: KeyOrPathArray<T>, header: ReactNode, decimalPlaces = 2): ReportColumnParts<T> {
		return commonReportColumns.float(path, header, { decimalPlaces, suffix: "%" });
	},
	currencyAmount<T>(
		amountPath: KeyOrPathArray<T>,
		currencyPath: KeyOrPathArray<T>,
		header: ReactNode,
		decimalPlaces = 2,
	): ReportColumnParts<T> {
		return {
			name: pathToStr(amountPath),
			orderable: false,
			align: "end",
			cellClassName: "tabular-nums",
			header: reportHeader<T>(header),
			content: (row, props) => {
				const amount = accessByPath(row, amountPath);
				if (amount == null) {
					return <TinyTableDataCell {...props}>{PLACEHOLDER}</TinyTableDataCell>;
				}
				const currency = accessByPath(row, currencyPath);
				const formatted = formatNumber(amount as number, decimalPlaces);
				return <TinyTableDataCell {...props}>{currency ? `${formatted} ${currency}` : formatted}</TinyTableDataCell>;
			},
		};
	},
};
